/**
 * TurnInCardsModal — fullscreen modal driven by CardHudButton.
 *
 * Shows the viewer's hand as a single non-scrolling row of WarCarp-themed
 * card pictograms, sized dynamically from the viewport so all (up to 5)
 * cards are visible at once in both portrait and landscape. Tap to toggle
 * selection (capped at 3). When the chosen trio forms a valid classic-style
 * set, the primary action enables and shows the next bonus preview. After
 * a successful turn-in the modal stays open only when the authoritative
 * response contains another valid set, so multiple sets can be processed
 * back-to-back without leaving a dead dialog behind.
 */

import React from 'react'
import { COMPACT_HEADER_STYLE, INK_COLOR } from '../../lib/gameChrome'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useTheme } from '../../hooks/useTheme'
import {
  View,
  Text,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native'
import { EdgeSheet } from '../ui/EdgeSheet'
import type { Card, CardBonusSchedule } from '@warcarp/api-types'
import { Button } from '../ui/Button'
import { useToast } from '../ui/Toast'
import { useTurnInCards } from '../../hooks/useGames'
import { useWebModalHistory } from '../../hooks/useWebModalHistory'
import { cardImagery, isValidSet, shouldCloseAfterTurnIn, nextCardBonus, cardTurnInStatus } from '../../lib/cards'
import { FORCED_TURN_IN_THRESHOLD } from '../../lib/cardTurnIn'
import { TutorialHelpStrip } from '../game/TutorialHelpStrip'
import { planCardRows, splitHandIntoRows, cardFaceLayout } from '../../lib/cardLayout'
import { breakableName, fitCardName } from '../../lib/cardNameFit'
import { teammateHands } from '../../lib/teamPlay'
import { SegmentedToggle } from '../ui/SegmentedToggle'
import type { GameState, Territory } from '../../lib/gameState'

interface TurnInCardsModalProps {
  visible: boolean
  onClose: () => void
  gameId: string
  state: GameState
  myPlayerId: string
  /** False outside the viewer's own placement phase; the hand remains readable. */
  canTurnInCards: boolean
  tutorialEnabled: boolean
  onTutorialEnabledChange: (enabled: boolean) => void
  /**
   * Portrait: window y of the game top bar's bottom edge. The modal then
   * leaves the top bar showing (greyed under a dim strip) and starts below
   * it, instead of covering the whole screen.
   */
  keepTopVisible?: number | null
  /**
   * Desktop/tablet layouts: width of the side column to leave showing (greyed)
   * so the sheet covers only the map area instead of the whole screen.
   */
  keepLeftVisible?: number | null
  /**
   * Team games: a teammate's display name, for the read-only teammate hands
   * (decision 10). Teammates' hands show only when this is given.
   */
  playerName?: (playerId: string) => string
}

export function TurnInCardsModal({
  visible,
  onClose,
  gameId,
  state,
  myPlayerId,
  canTurnInCards: canTurnInOwn,
  tutorialEnabled,
  onTutorialEnabledChange,
  keepTopVisible = null,
  keepLeftVisible = null,
  playerName,
}: TurnInCardsModalProps) {
  const { showToast } = useToast()
  const { theme } = useTheme()
  const turnIn = useTurnInCards(gameId)
  const { width: screenWidth, height: screenHeight, fontScale } = useWindowDimensions()
  const isLandscape = screenWidth > screenHeight
  // The sheet's own width (the map area when the side column stays visible).
  const sheetWidth = screenWidth - (keepLeftVisible ?? 0)
  // Team games: the viewer can flip to a teammate's hand, read-only. Only the
  // owner can turn cards in (decision 10). Free-for-all: no teammates, so the
  // sheet is unchanged.
  const teammates = React.useMemo(
    () => (playerName ? teammateHands(state, myPlayerId) : []),
    [playerName, state, myPlayerId],
  )
  const [viewingId, setViewingId] = React.useState<string>(myPlayerId)
  React.useEffect(() => {
    if (!visible) setViewingId(myPlayerId)
  }, [visible, myPlayerId])
  const viewingTeammate = viewingId !== myPlayerId && teammates.some((t) => t.playerId === viewingId)
  const viewedName = viewingTeammate && playerName ? playerName(viewingId) : null
  const hand = React.useMemo(
    () => state.cards?.[viewingTeammate ? viewingId : myPlayerId] ?? [],
    [state.cards, myPlayerId, viewingId, viewingTeammate],
  )
  // A teammate's hand is never selectable, whatever phase the viewer is in.
  const canTurnInCards = canTurnInOwn && !viewingTeammate
  const mustTurnIn = canTurnInOwn && state.mustTurnInCards === true
  const schedule: CardBonusSchedule =
    state.config.cardBonusSchedule ?? 'cycle'
  const turnInsSoFar = state.cardTurnIns ?? 0
  const bonusPreview = nextCardBonus(schedule, turnInsSoFar)

  // Selected card IDs. Reset whenever the modal opens or the hand
  // changes (e.g. after a successful turn-in re-fetches state).
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])
  React.useEffect(() => {
    if (!visible) setSelectedIds([])
  }, [visible])
  React.useEffect(() => {
    if (!canTurnInCards) setSelectedIds([])
  }, [canTurnInCards])
  // Drop any selection IDs that no longer exist in the hand (e.g.
  // after a successful turn-in or an opponent action that races).
  React.useEffect(() => {
    setSelectedIds((prev) => prev.filter((id) => hand.some((c) => c.id === id)))
  }, [hand])

  function toggle(cardId: string) {
    if (!canTurnInCards) return
    setSelectedIds((prev) => {
      if (prev.includes(cardId)) return prev.filter((x) => x !== cardId)
      if (prev.length >= 3) return prev // cap at 3
      return [...prev, cardId]
    })
  }

  const selectedCards = selectedIds
    .map((id) => hand.find((c) => c.id === id))
    .filter((c): c is Card => !!c)
  const selectionValid =
    selectedCards.length === 3 && isValidSet(selectedCards)

  const statusLine = viewedName
    ? `${viewedName}'s hand. Only ${viewedName} can turn these in.`
    : cardTurnInStatus(hand, selectedCards, canTurnInCards)

  async function handleSubmit() {
    if (!canTurnInCards || !selectionValid) return
    try {
      const updatedGame = await turnIn.mutateAsync({ cardIds: selectedIds })
      setSelectedIds([])
      // React Query writes updatedGame to the cache before mutateAsync
      // resumes. Decide from that response directly: an effect watching the
      // rendered hand can miss the transition and strand a setless modal.
      if (shouldCloseAfterTurnIn(updatedGame.state, myPlayerId)) onClose()
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : 'Failed to turn in cards',
        'error',
      )
    }
  }

  // Close-button gating. A GENUINE forced turn-in (flag set AND ≥5 cards held)
  // stays non-closeable — the player must trade in before placing. But if the
  // flag is set on a sub-threshold hand (a stale/bled `mustTurnInCards` — e.g.
  // a resigning player's flag bleeding onto the next player, fixed in
  // engine/resign.ts), the hand can't form a set, so locking the modal would
  // soft-lock the player. In that case the modal stays closeable as an escape
  // hatch. The server still gates placement on the turn-in, so closing never
  // skips the rule.
  const closeDisabled = mustTurnIn && (state.cards?.[myPlayerId]?.length ?? 0) >= FORCED_TURN_IN_THRESHOLD
  const handleCloseRequest = () => {
    if (closeDisabled) return
    onClose()
  }

  useWebModalHistory({
    visible,
    canDismiss: !closeDisabled,
    onDismiss: handleCloseRequest,
  })

  // Card sizing — fit the hand without scrolling, inside the vertical space
  // left after the header, schedule strip, and action row. A large forced
  // hand (>6) in portrait wraps onto 2 rows so cards don't shrink to slivers;
  // landscape always stays one row. Width is budgeted per row; the vertical
  // budget is split across the row(s). Cell count is max(_, 5) so the cell
  // stays stable when the per-row count is small.
  const HPAD = 16
  const GAP = 8
  const { rowCount, cardsPerRow } = planCardRows(hand.length, isLandscape)
  // Floor the per-row cell count so cards stay a stable size as the hand
  // grows. Portrait uses a lower floor (4) than landscape (5) so a typical
  // small hand renders wider — wide enough to read full territory/accent
  // names without truncation.
  const cellCount = Math.max(cardsPerRow, isLandscape ? 5 : 4)
  const widthBudget = Math.floor(
    (sheetWidth - HPAD * 2 - GAP * (cellCount - 1)) / cellCount,
  )
  // Vertical budget — derived from the ACTUAL measured height of the hand
  // container rather than a guessed chrome constant, so cards never overflow
  // the header / bonus-strip bounds (especially tight in landscape). Until
  // the first onLayout lands we fall back to a conservative estimate.
  const VPAD = isLandscape ? 8 : 12
  const [handBoxHeight, setHandBoxHeight] = React.useState(0)
  const [statusHeight, setStatusHeight] = React.useState(40)
  const FALLBACK_CHROME = 240
  const availableHeight =
    handBoxHeight > 0
      ? Math.max(0, handBoxHeight - VPAD * 2 - statusHeight - GAP)
      : Math.max(96, screenHeight - FALLBACK_CHROME - statusHeight - GAP)
  // Each row gets an equal slice of the available height (minus inter-row gap),
  // so two stacked rows still fit within the container.
  const heightBudget = Math.floor((availableHeight - GAP * (rowCount - 1)) / rowCount)
  // Prefer 1.5×width aspect, but the per-row height budget is a HARD ceiling
  // — the soft floor never wins over it, so cards can't overflow the bounds.
  const naturalHeight = Math.round(widthBudget * 1.5)
  const cardHeight = Math.min(
    heightBudget,
    Math.max(90, Math.min(280, naturalHeight)),
  )
  // If height was clamped by the vertical budget, shrink width too so
  // the card keeps a reasonable aspect ratio (and the emoji doesn't
  // look stretched). Otherwise stick with the natural width.
  const cardWidth = Math.max(
    56,
    Math.min(200, widthBudget, Math.round(cardHeight / 1.5)),
  )
  const { padding, emojiSize, typeLabelSize, accentLabelSize, typeLineHeight, accentLineHeight,
    territoryBase, territoryMin, nameBandHeight, topHeight, nameWidth } = cardFaceLayout(cardWidth, cardHeight, fontScale)

  // territory name lookup so cards can display the associated region.
  const territoryName = (territoryId?: string): string | null => {
    if (!territoryId) return null
    const t = state.map.territories.find(
      (x: Territory) => x.id === territoryId,
    )
    return t?.name ?? null
  }

  // The hand split into rows (1 normally; 2 for a big portrait hand).
  const cardRows = splitHandIntoRows(hand, isLandscape)

  const renderCard = (card: Card) => {
    const selected = selectedIds.includes(card.id)
    const imagery = cardImagery(card.type)
    const territory = territoryName(card.territoryId)
    const nameFit = territory
      ? fitCardName(territory, { width: nameWidth, height: nameBandHeight, base: territoryBase, min: territoryMin, fontScale })
      : null
    return (
      <TouchableOpacity
        key={card.id}
        onPress={() => toggle(card.id)}
        disabled={!canTurnInCards}
        accessibilityRole="button"
        accessibilityLabel={`${imagery.label} card${
          territory ? ` (${territory})` : ''
        }, ${selected ? 'selected' : 'not selected'}`}
        accessibilityState={{ selected, disabled: !canTurnInCards }}
        className={`bg-paper-up border-2 rounded-lg items-center ${
          selected ? 'border-blood' : 'border-olive'
        }`}
        style={{
          width: cardWidth,
          height: cardHeight,
          padding,
          transform: [{ scale: selected ? 1.04 : 1 }],
        }}
      >
        {/* Fixed top zone: type, art and title sit at the same height on every
            card regardless of the territory name below. */}
        <View className="items-center justify-between" style={{ height: topHeight, alignSelf: 'stretch' }}>
          <Text
            className="font-stencil uppercase text-ink-soft text-center"
            style={{ fontSize: typeLabelSize, lineHeight: typeLineHeight, alignSelf: 'stretch' }}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.6}
          >
            {card.type}
          </Text>
          {/* Android text measurement rejects fontSize 0 during short layouts. */}
          {emojiSize > 0 && <Text allowFontScaling={false} style={{ fontSize: emojiSize, lineHeight: emojiSize, includeFontPadding: false, ...(imagery.emojiDrop ? { transform: [{ translateY: Math.round(emojiSize * imagery.emojiDrop) }] } : null) }}>{imagery.emoji}</Text>}
          <Text
            className={`font-stencil uppercase ${imagery.accentColor}`}
            style={{ fontSize: accentLabelSize, lineHeight: accentLineHeight, alignSelf: 'stretch', textAlign: 'center' }}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.6}
          >
            {imagery.label}
          </Text>
        </View>
        {/* Fixed name band. The name is pre-fitted (cardNameFit) to two lines,
            shrinking to a floor before ellipsizing; the size already includes
            the system font scale, so scaling is disabled here. Wild cards
            leave the band empty. */}
        <View className="items-center justify-center" style={{ height: nameBandHeight, alignSelf: 'stretch' }}>
          {territory && nameFit && (
            <Text
              testID={`card-territory-${card.id}`}
              className="font-typed text-ink-soft italic text-center"
              allowFontScaling={false}
              style={{ fontSize: nameFit.fontSize, lineHeight: nameFit.lineHeight, includeFontPadding: false, alignSelf: 'stretch', paddingHorizontal: 2 }}
              numberOfLines={2}
            >
              {breakableName(territory)}
            </Text>
          )}
        </View>
      </TouchableOpacity>
    )
  }

  return (
    <EdgeSheet
      visible={visible}
      onRequestClose={handleCloseRequest}
      closeAccessibilityLabel="Close your cards"
      testIDPrefix="cards-modal"
      keepTopVisible={keepTopVisible}
      keepLeftVisible={keepLeftVisible}
    >
        {/* ── Header ─────────────────────────────────────────────── */}
        <View
          style={COMPACT_HEADER_STYLE}
          className={`flex-row items-center justify-between px-4 border-b ${
            mustTurnIn ? 'border-blood' : 'border-olive'
          }`}
        >
          <Text
            className={`font-stencil text-base uppercase flex-1 ${
              mustTurnIn ? 'text-blood' : 'text-ink'
            }`}
            numberOfLines={1}
          >
            {viewedName
              ? `${viewedName}'s cards (${hand.length}/5)`
              : mustTurnIn
              ? `Turn in a set — ${hand.length} cards held`
              : `Your cards (${hand.length}/5)`}
          </Text>
          <TouchableOpacity
            onPress={handleCloseRequest}
            disabled={closeDisabled}
            accessibilityLabel="Close cards"
            accessibilityRole="button"
            className="py-1"
            style={{
              minHeight: 40,
              minWidth: 40,
              // Glyph flush right, on the sheet's 16px gutter (the touch box
              // keeps its 40px width, extending inward).
              alignItems: 'flex-end',
              justifyContent: 'center',
              opacity: closeDisabled ? 0.3 : 1,
            }}
          >
            {/* Same close icon and ink colour as the other sheets/drawers
                (the touch box's opacity dims it while closing is locked). */}
            <MaterialCommunityIcons name="close" size={24} color={INK_COLOR[theme === 'dark' ? 'dark' : 'light']} />
          </TouchableOpacity>
        </View>

        {/* ── Team games: whose hand ────────────────────────────── */}
        {teammates.length > 0 && playerName && (
          <View className="flex-row items-center justify-center px-4 pt-2" testID="cards-modal-hands">
            <SegmentedToggle
              options={[
                { value: myPlayerId, label: 'Yours', accessibilityLabel: 'Show your cards', testID: 'cards-modal-hand-mine' },
                ...teammates.map((t) => ({
                  value: t.playerId,
                  label: `${playerName(t.playerId)} (${t.cards.length})`,
                  accessibilityLabel: `Show ${playerName(t.playerId)}'s cards`,
                  testID: `cards-modal-hand-${t.playerId}`,
                })),
              ]}
              value={viewingTeammate ? viewingId : myPlayerId}
              onChange={setViewingId}
              size={30}
              minWidth={72}
            />
          </View>
        )}

        {/* ── Hand ───────────────────────────────────────────────── */}
        <View
          onLayout={(e) => setHandBoxHeight(e.nativeEvent.layout.height)}
          style={{
            flex: 1,
            justifyContent: 'center',
            paddingHorizontal: HPAD,
            paddingVertical: VPAD,
            gap: GAP,
          }}
        >
          {hand.length === 0 ? (
            <View className="items-center justify-center px-6 py-8">
              <Text className="text-ink-soft font-typed italic text-center">
                {viewedName
                  ? `${viewedName} has no cards yet.`
                  : "No cards yet. Conquer a territory on your turn and you'll draw one when the turn ends."}
              </Text>
            </View>
          ) : (
            cardRows.map((row, rowIndex) => (
              <View
                key={rowIndex}
                className="flex-row items-center justify-center"
                style={{ gap: GAP }}
              >
                {row.map(renderCard)}
              </View>
            ))
          )}
          <Text
            testID="card-turn-in-status"
            onLayout={(event) => setStatusHeight(event.nativeEvent.layout.height)}
            className={`font-typed text-sm text-center ${
              selectedCards.length === 3 && !selectionValid ? 'text-blood' : 'text-ink'
            }`}
          >
            {statusLine}
          </Text>
        </View>

        {/* Tip sits at the bottom, on top of the set-bonus bar. */}
        {tutorialEnabled ? (
          <TutorialHelpStrip
            copy={{
              title: 'Card sets',
              body: 'A set is 3 cards of the same type or 1 of each type. Wild cards can stand in for any type. During placement, tap 3 cards, then Turn in to receive the set bonus.',
            }}
            onDisable={() => onTutorialEnabledChange(false)}
            edge="top"
          />
        ) : null}

        {/* ── Bonus schedule preview ─────────────────────────────── */}
        <View className="px-4 pt-1.5 pb-2 gap-1 border-t border-olive">
          <View className="flex-row items-baseline justify-between">
            <Text className="font-stencil text-[10px] uppercase tracking-widest text-ink-soft">
              Set bonus
            </Text>
            <Text className="font-typed text-[10px] italic text-ink-soft">
              // {schedule === 'cycle' ? 'cycles 4,6,8,10' : 'increments by 1'}
            </Text>
          </View>
          <View className="flex-row gap-1">
            {Array.from({ length: 6 }).map((_, i) => {
              const turnInIndex = turnInsSoFar + i
              const value = nextCardBonus(schedule, turnInIndex)
              const isCurrent = i === 0
              return (
                <View
                  key={i}
                  className={`flex-1 items-center justify-center rounded-md py-0.5 border ${
                    isCurrent
                      ? 'bg-blood border-ink'
                      : 'bg-paper-up border-olive'
                  }`}
                  accessibilityLabel={
                    isCurrent
                      ? `Next turn-in bonus: ${value}`
                      : `Turn-in #${turnInIndex + 1}: ${value}`
                  }
                >
                  <Text
                    className={`font-stencil text-sm ${
                      isCurrent ? 'text-paper' : 'text-ink'
                    }`}
                  >
                    +{value}
                  </Text>
                </View>
              )
            })}
          </View>
        </View>

        {/* ── Action ─────────────────────────────────────────────── */}
        <View className="flex-row items-center justify-end px-4 pt-2 pb-3 border-t border-olive">
          <Button
            title={
              selectionValid
                ? `Turn in (+${bonusPreview})`
                : 'Turn in'
            }
            onPress={handleSubmit}
            disabled={!canTurnInCards || !selectionValid || turnIn.isPending}
            loading={turnIn.isPending}
            accessibilityLabel="Turn in selected cards"
          />
        </View>
    </EdgeSheet>
  )
}
